import { ReactNode } from "react";
import { styled } from "@mui/material";

import { Serie } from "@/types/serie";
import TimeRange from "@/types/time-range";

import Footer from "./footer";
import { WarningColors, PlottedTimeSerie, WarningColor } from "./types";
import * as utils from "./table.utils";

const StyledTable = styled("table")({
  fontSize: "0.625rem",
  fontWeight: "normal",
  color: "#3a3a3a",
  width: "100%",
  tableLayout: "fixed",
  borderCollapse: "collapse",
  textAlign: "center",
});

const StyledTableHead = styled("th")({
  fontStyle: "italic",
  fontWeight: "normal",
});

const StyledTableCell = styled("td")<{
  colorCode: WarningColor;
}>(({ colorCode, theme }) => {
  const backgroundColor = colorCode != "none" ? theme.palette[colorCode]?.main : "white";
  const hasBackground = backgroundColor !== "white";

  const border = `2px solid ${hasBackground ? "white" : "transparent"}`;

  /*
    Color is dependent on background. Text has a bit of transparency,
    on hover the transparency is removed.
  /*/
  const color = hasBackground ? "white" : "#3a3a3a";
  const textColor = hasBackground ? "white" : `${color}66`;

  return {
    color: textColor,
    backgroundColor,
    border,
    padding: "3px",
    verticalAlign: "middle",
    fontSize: "0.75rem",

    ":hover": {
      color,
      fontWeight: "bold",
    },
  };
});

type Props = {
  /** Set the cells background color for a value */
  getColorCodeForValue?: (value: number | string) => WarningColor;
  /** Overwrite de default render value of `${value}${unit}` */
  valueRenderer?: (value: number, serie: Serie, date: Date) => ReactNode;
  /** Overwrite de default render value of `-` */
  emptyValueRenderer?: () => string;
  /** What should be show in the tooltip */
  getTooltip?: (value: number, serie: Serie, date: Date) => string;
  /** If true values will be hidden when there er to many items */
  tableHeadFormatter?: (serie: Serie) => string;
  alwaysShowValues?: boolean;
  timeRange: TimeRange;
  series: Serie[];
};

const TimeSeriesTable = ({
  getColorCodeForValue,
  valueRenderer,
  emptyValueRenderer,
  tableHeadFormatter,
  getTooltip,
  timeRange,
  series,
  alwaysShowValues = false,
}: Props) => {
  const dateTimeKeys = utils.createDateTimeKeys(timeRange);

  const getColorCode = (value: number | string) => {
    if (getColorCodeForValue) return getColorCodeForValue(value);
    return WarningColors.NONE;
  };

  const getTitle = (value: number, serie: Serie, date: Date) => {
    if (utils.isEmpty(value)) return "-";
    if (getTooltip) return getTooltip(value, serie, date);
    return `${value}${serie.units}`;
  };

  const renderValue = (value: number, serie: Serie, date: Date) => {
    // Hide the value if there are to many items
    if (dateTimeKeys.length > 13 && !alwaysShowValues) return "";

    if (utils.isEmpty(value)) {
      return emptyValueRenderer ? emptyValueRenderer() : "-";
    }

    if (valueRenderer) return valueRenderer(value, serie, date);

    return `${value}${serie.units}`;
  };

  const renderRow = (serie: Serie, data: PlottedTimeSerie) => {
    const { name } = serie;

    const tableHead = tableHeadFormatter?.(serie) || serie.name;

    let colSpan = 0;

    return (
      <tr key={`row-${name}`}>
        <StyledTableHead>{tableHead}</StyledTableHead>
        {dateTimeKeys.map((date) => {
          colSpan--;

          // The last point still spans this date
          if (colSpan > 0) return null;

          const point = data[date.toISOString()];
          colSpan = point?.span || 1;

          return (
            <StyledTableCell
              key={`${name}-${date}`}
              title={getTitle(point?.value, serie, date)}
              colSpan={colSpan}
              colorCode={getColorCode(point?.value)}
            >
              {renderValue(point?.value, serie, date)}
            </StyledTableCell>
          );
        })}
      </tr>
    );
  };

  return (
    <StyledTable>
      <tbody>
        {series.map((serie) => {
          return renderRow(serie, utils.plotSeriePoints(serie, timeRange));
        })}
      </tbody>
      <Footer dates={dateTimeKeys} />
    </StyledTable>
  );
};

export default TimeSeriesTable;
